import {
  type BigDecimal,
  createBigDecimal,
  divide,
  greaterThan,
  minus,
  plus,
  times,
  toStringBigDecimal,
} from '@0xtorch/big-decimal'
import type { CryptoCurrency, FiatCurrency, Nft, Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { DepositWithBond, Loan, LoanPosition } from '../../types/loan'
import type { Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import { mergeActionTransfers } from '../../utils/mergeActionTransfers'
import { createBlankTransaction } from './createBlankTransaction'
import { createFeeTransaction } from './createFeeTransaction'
import { createIncomeTransaction } from './createIncomeTransaction'
import { createRepaymentTransaction } from './createRepaymentTransaction'
import { createTransactionsByTradeAction } from './createTransactionsByTradeAction'
import type {
  CreateTransactionsByCategoryActionParameters,
  CreateTransactionsByCategoryActionReturnTypes,
} from './types'
import { createInsufficientTransactionByTransfer } from './util/createInsufficientTransactionByTransfer.ts'

type CreateTransactionsByWithdrawActionParameters =
  CreateTransactionsByCategoryActionParameters & {
    readonly costBasis: CostBasis
    readonly handleLoanAsTrade?: boolean
    readonly use5PercentRule?: boolean
  }

type CreateTransactionsByWithdrawActionReturnTypes =
  CreateTransactionsByCategoryActionReturnTypes

export const createTransactionsByWithdrawAction = ({
  actionId,
  action,
  loans,
  crossActionBundles,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  isLocked = false,
  handleLoanAsTrade = false,
  use5PercentRule = false,
}: CreateTransactionsByWithdrawActionParameters): CreateTransactionsByWithdrawActionReturnTypes => {
  // locked action の場合処理しない
  if (isLocked) {
    return {
      transactions: [],
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
    }
  }

  if (handleLoanAsTrade) {
    return createTransactionsByTradeAction({
      actionId,
      action,
      loans,
      crossActionBundles,
      assetBalances,
      assetBorrows,
      usedBalances,
      nftAcquiredTimestamps,
      isLocked,
      costBasis,
      use5PercentRule,
    })
  }

  if (action.loanId === undefined) {
    action.loanId = ''
  }

  // merge transfers
  const transfers = mergeActionTransfers(action.transfers)
  const transferCount: { all: number; in: number; out: number } = {
    all: 0,
    in: 0,
    out: 0,
  }
  const inTransfers: Transfer[] = []
  const outTransfers: Transfer[] = []
  for (const transfer of transfers) {
    transferCount.all += 1
    if (transfer.direction === 'in') {
      transferCount.in += 1
      inTransfers.push(transfer)
    }
    if (transfer.direction === 'out') {
      transferCount.out += 1
      outTransfers.push(transfer)
    }
  }

  let newAssetBalances = assetBalances
  let newAssetBorrows = assetBorrows
  let newUsedBalances = usedBalances
  const transactions: Transaction[] = []

  if (transferCount.all === 0) {
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
    return {
      transactions,
      loans,
      crossActionBundles,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    }
  }

  // update loan
  const loan: Loan = loans.get(action.loanId) ?? {
    id: action.loanId,
    borrows: new Map(),
    borrowsWithDebt: new Map(),
    deposits: new Map(),
    depositsWithBond: new Map(),
  }
  const bond = transferCount.out === 1 ? outTransfers[0] : undefined
  const positions = inTransfers
  const repaymentTransfers: Transfer[] = []
  const withdrawTransfers: Transfer[] = []
  const interestTransfers: Transfer[] = []
  const interestFeeTransfers: Transfer[] = []
  if (bond === undefined) {
    // position 毎に処理
    for (const position of positions) {
      // 引き出し分を計算
      const depositAssetKey = createAssetKey(position.asset)
      const deposits = loan.deposits.get(depositAssetKey) ?? []
      const newDeposits: LoanPosition[] = []
      let remainAmount = position.amount
      let withdrawAmount = createBigDecimal(0n)
      for (const deposit of deposits) {
        if (remainAmount.value === 0n) {
          newDeposits.push(deposit)
        }
        // remain amount >= deposit amount
        else if (!greaterThan(deposit.amount, remainAmount)) {
          remainAmount = minus(remainAmount, deposit.amount)
          withdrawAmount = plus(withdrawAmount, deposit.amount)
        }
        // remain amount < deposit amount
        else {
          const newDeposit: LoanPosition = {
            asset: deposit.asset,
            amount: minus(deposit.amount, remainAmount),
          }
          withdrawAmount = plus(withdrawAmount, remainAmount)
          remainAmount = createBigDecimal(0n)
          newDeposits.push(newDeposit)
        }
      }
      if (newDeposits.length > 0) {
        loan.deposits.set(depositAssetKey, newDeposits)
      } else {
        loan.deposits.delete(depositAssetKey)
      }

      // withdrawAmount 分を withdraw として処理
      if (withdrawAmount.value > 0n) {
        withdrawTransfers.push({
          ...position,
          amount: withdrawAmount,
        })
      }

      // position.amount > withdrawAmount の場合、差額を interest として処理
      if (greaterThan(position.amount, withdrawAmount)) {
        interestTransfers.push({
          ...position,
          amount: minus(position.amount, withdrawAmount),
        })
      }
    }
  } else {
    // 引き出し分を計算
    const bondAssetKey = createAssetKey(bond.asset)
    const depositsWithBond = loan.depositsWithBond.get(bondAssetKey) ?? []
    const newDepositsWithBond: DepositWithBond[] = []
    let remainBondAmount = bond.amount
    let repaymentAmount = createBigDecimal(0n)
    const withdrawAmounts = new Map<string, BigDecimal>()
    for (const deposit of depositsWithBond) {
      if (deposit.bond.amount.value === 0n) {
        continue
      }
      if (remainBondAmount.value === 0n) {
        newDepositsWithBond.push(deposit)
      }
      // remain amount >= deposit bond amount
      else if (!greaterThan(deposit.bond.amount, remainBondAmount)) {
        remainBondAmount = minus(remainBondAmount, deposit.bond.amount)
        repaymentAmount = plus(repaymentAmount, deposit.bond.amount)
        for (const position of deposit.positions) {
          withdrawAmounts.set(
            position.asset,
            plus(
              withdrawAmounts.get(position.asset) ?? createBigDecimal(0n),
              position.amount,
            ),
          )
        }
      }
      // remain amount < deposit bond amount
      else {
        const bondAmount = remainBondAmount
        const newDeposit: DepositWithBond = {
          positions: [],
          bond: {
            asset: deposit.bond.asset,
            amount: minus(deposit.bond.amount, bondAmount),
          },
        }
        remainBondAmount = createBigDecimal(0n)
        repaymentAmount = plus(repaymentAmount, bondAmount)
        for (const position of deposit.positions) {
          // position.amount * (bondAmount / deposit.bond.amount)
          const withdrawAmount = divide(
            times(position.amount, bondAmount, 18),
            deposit.bond.amount,
            18,
          )
          withdrawAmounts.set(
            position.asset,
            plus(
              withdrawAmounts.get(position.asset) ?? createBigDecimal(0n),
              withdrawAmount,
            ),
          )
          newDeposit.positions.push({
            asset: position.asset,
            amount: minus(position.amount, withdrawAmount),
          })
        }
        newDepositsWithBond.push(newDeposit)
      }
    }
    if (newDepositsWithBond.length > 0) {
      loan.depositsWithBond.set(bondAssetKey, newDepositsWithBond)
    } else {
      loan.depositsWithBond.delete(bondAssetKey)
    }

    // repaymentAmount を repayment として処理
    if (repaymentAmount.value > 0n) {
      repaymentTransfers.push({
        ...bond,
        amount: repaymentAmount,
      })
    }

    // bond の余剰分を interest-fee として処理
    if (greaterThan(bond.amount, repaymentAmount)) {
      interestFeeTransfers.push({
        ...bond,
        amount: minus(bond.amount, repaymentAmount),
      })
    }

    // position 毎に処理
    const positionAssetKeys = new Set<string>()
    for (const position of positions) {
      const key = createAssetKey(position.asset)
      positionAssetKeys.add(key)
      // withdrawAmount 分を withdraw として処理
      const withdrawAmount = withdrawAmounts.get(key) ?? createBigDecimal(0n)
      if (withdrawAmount.value > 0n) {
        withdrawTransfers.push({
          ...position,
          amount: withdrawAmount,
        })
      }

      // position.amount > withdrawAmount の場合、差額を interest として処理
      if (greaterThan(position.amount, withdrawAmount)) {
        interestTransfers.push({
          ...position,
          amount: minus(position.amount, withdrawAmount),
        })
      }
      // position.amount < withdrawAmount の場合、差額を interest-fee として処理
      else if (greaterThan(withdrawAmount, position.amount)) {
        interestFeeTransfers.push({
          ...position,
          amount: minus(withdrawAmount, position.amount),
        })
      }
    }
    // withdrawAmounts のうち、 positions に存在しない asset を interest-fee として処理
    for (const [assetKey, withdrawAmount] of withdrawAmounts.entries()) {
      if (positionAssetKeys.has(assetKey) || withdrawAmount.value <= 0n) {
        continue
      }
      interestFeeTransfers.push({
        asset: createAssetByAssetKey(assetKey),
        amount: withdrawAmount,
        direction: 'out',
      })
    }
  }
  if (
    loan.borrows.size === 0 &&
    loan.borrowsWithDebt.size === 0 &&
    loan.deposits.size === 0 &&
    loan.depositsWithBond.size === 0
  ) {
    loans.delete(action.loanId)
  } else {
    loans.set(action.loanId, loan)
  }

  for (const transfer of withdrawTransfers) {
    // withdraw は blank 取引を追加して、 asset 変化はさせない
    const result = createBlankTransaction({
      actionId,
      timestamp: action.timestamp,
      comment: `Withdraw ${toStringBigDecimal(transfer.amount)} [${'symbol' in transfer.asset ? transfer.asset.symbol : (transfer.asset.name ?? 'Unknown NFT')}]`,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of interestTransfers) {
    const result = createIncomeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: 'interest',
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of repaymentTransfers) {
    // 不足する場合は insufficient 取引を追加
    const insufficientResult = createInsufficientTransactionByTransfer({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
      onlyUseBorrows: true,
    })
    if (insufficientResult !== undefined) {
      transactions.push(insufficientResult.transaction)
      newAssetBalances = insufficientResult.assetBalances
      newAssetBorrows = insufficientResult.assetBorrows
      newUsedBalances = insufficientResult.usedBalances
    }

    const result = createRepaymentTransaction({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  for (const transfer of interestFeeTransfers) {
    // 不足する場合は insufficient 取引を追加
    const insufficientResult = createInsufficientTransactionByTransfer({
      actionId,
      timestamp: action.timestamp,
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
    })
    if (insufficientResult !== undefined) {
      transactions.push(insufficientResult.transaction)
      newAssetBalances = insufficientResult.assetBalances
      newAssetBorrows = insufficientResult.assetBorrows
      newUsedBalances = insufficientResult.usedBalances
    }

    const result = createFeeTransaction({
      actionId,
      timestamp: action.timestamp,
      subCategory: 'interest-fee',
      transfer,
      assetBalances: newAssetBalances,
      assetBorrows: newAssetBorrows,
      usedBalances: newUsedBalances,
      costBasis,
      use5PercentRule,
    })
    transactions.push(result.transaction)
    newAssetBalances = result.assetBalances
    newAssetBorrows = result.assetBorrows
    newUsedBalances = result.usedBalances
  }

  return {
    transactions,
    loans,
    crossActionBundles,
    assetBalances: newAssetBalances,
    assetBorrows: newAssetBorrows,
    usedBalances: newUsedBalances,
  }
}

const createAssetByAssetKey = (
  assetKey: string,
): CryptoCurrency | FiatCurrency | Nft => {
  const [type, id] = assetKey.split('/')
  switch (type) {
    case 'CryptoCurrency': {
      return {
        symbol: '',
        type,
        id,
        name: '',
        updatedAt: 0,
      }
    }
    case 'FiatCurrency': {
      return {
        symbol: '',
        type,
        id: id as FiatCurrency['id'],
        name: '',
      }
    }
    case 'Nft': {
      return {
        type,
        id,
        updatedAt: 0,
      }
    }
    default: {
      throw new Error(`Invalid assetKey: ${assetKey}`)
    }
  }
}
